import type { MouseEventHandler, ReactNode } from "react";

import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import { usePurchaseFlowAnchorProps } from "@/react/helpers/purchaseFlowAnchor";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

export interface CTAButtonProps {
  children: ReactNode;
  color?: string;
  isFullWidth?: boolean;
  mayLeadToPurchase?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  textColor?: string;
  url: string;
}

export const CTAButton = ({
  children,
  color,
  isFullWidth = false,
  mayLeadToPurchase = false,
  onClick,
  textColor,
  url,
}: CTAButtonProps) => {
  const { isCircleV4 } = useCircleV4();
  const purchaseFlowProps = usePurchaseFlowAnchorProps(mayLeadToPurchase);
  const legacyClasses = [
    "focus-visible:!outline-secondary group block rounded-full px-6 py-2.5 font-bold focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2",
    !isFullWidth && "w-auto",
    isFullWidth && "text-center",
    !textColor && "!text-white",
  ];
  const circleV4Classes = [
    "bg-inverse text-inverse inline-flex min-h-9 max-w-full shrink-0 items-center justify-center gap-2 whitespace-normal rounded-md px-4 py-2 text-center text-label-sm no-underline shadow-2xs [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:shadow-focus",
    !isFullWidth && "w-fit",
  ];

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-anchor={isCircleV4 ? "tiptap-cta" : undefined}
      {...purchaseFlowProps}
      style={{ backgroundColor: color, color: textColor }}
      className={classNames(
        anchorClassNames("tiptap-cta"),
        "relative transition-opacity hover:opacity-90",
        isFullWidth && "w-[350px]",
        isCircleV4 ? circleV4Classes : legacyClasses,
      )}
      onClick={onClick}
    >
      {children}
    </a>
  );
};
